import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { settingsApi } from '../services/api';

const SettingsContext = createContext(null);

// ── Defaults ──────────────────────────────────────────────────────────────────
export const SETTINGS_DEFAULTS = {
  theme:        'light',
  currency:     'USD',
  numberFormat: '1,234.56',
  dateFormat:   'MM/DD/YYYY',
  showCents:    true,
  tableDensity: 'Comfortable',
  fiscalMonth:  'January',
};

const CURRENCY_SYMBOLS = {
  USD: '$', GBP: '£', EUR: '€', CAD: 'C$', AUD: 'A$', JPY: '¥',
};

// ── Theme application ─────────────────────────────────────────────────────────
function applyTheme(theme) {
  const root = document.documentElement;
  if (theme === 'system') {
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    root.classList.toggle('dark', prefersDark);
  } else {
    root.classList.toggle('dark', theme === 'dark');
  }
}

// ── Read cached settings from localStorage (used for instant init) ─────────────
function getCachedSettings() {
  try {
    const raw = localStorage.getItem('app_settings');
    return raw ? { ...SETTINGS_DEFAULTS, ...JSON.parse(raw) } : { ...SETTINGS_DEFAULTS };
  } catch {
    return { ...SETTINGS_DEFAULTS };
  }
}

// ── Provider ──────────────────────────────────────────────────────────────────
export function SettingsProvider({ children }) {
  const { isAuthenticated } = useAuth();

  // Initialize from localStorage immediately so theme is applied before first render
  const [settings, setSettings] = useState(getCachedSettings);

  // Apply theme whenever it changes
  useEffect(() => {
    applyTheme(settings.theme);

    // If system, also watch for OS preference changes
    if (settings.theme === 'system') {
      const mq = window.matchMedia('(prefers-color-scheme: dark)');
      const handler = () => applyTheme('system');
      mq.addEventListener('change', handler);
      return () => mq.removeEventListener('change', handler);
    }
  }, [settings.theme]);

  // Fetch from API whenever auth state changes
  useEffect(() => {
    if (!isAuthenticated) return;

    settingsApi?.getAll()
      ?.then((res) => {
        const s = res?.data?.data || {};
        const loaded = {
          theme:        s.theme               || SETTINGS_DEFAULTS.theme,
          currency:     s.display_currency    || SETTINGS_DEFAULTS.currency,
          numberFormat: s.number_format       || SETTINGS_DEFAULTS.numberFormat,
          dateFormat:   s.date_format         || SETTINGS_DEFAULTS.dateFormat,
          showCents:    s.show_cents !== undefined
            ? (s.show_cents === 'true' || s.show_cents === true)
            : SETTINGS_DEFAULTS.showCents,
          tableDensity: s.table_density       || SETTINGS_DEFAULTS.tableDensity,
          fiscalMonth:  s.fiscal_month        || SETTINGS_DEFAULTS.fiscalMonth,
        };
        setSettings(loaded);
        localStorage.setItem('app_settings', JSON.stringify(loaded));
      })
      ?.catch(() => {});
  }, [isAuthenticated]);

  // Called by settings pages after a successful save so changes apply immediately
  const updateSettings = useCallback((partial) => {
    setSettings((prev) => {
      const merged = { ...prev, ...partial };
      localStorage.setItem('app_settings', JSON.stringify(merged));
      return merged;
    });
  }, []);

  // ── Formatting helpers ──────────────────────────────────────────────────────

  const formatCurrency = useCallback((amount, opts = {}) => {
    const num = parseFloat(amount) || 0;
    const symbol = CURRENCY_SYMBOLS[settings.currency] || '$';
    const useCents = opts.noCents ? false : settings.showCents;

    if (settings.numberFormat === '1.234,56') {
      const formatted = num.toLocaleString('de-DE', {
        minimumFractionDigits: useCents ? 2 : 0,
        maximumFractionDigits: useCents ? 2 : 0,
      });
      return `${symbol}${formatted}`;
    }

    const formatted = num.toLocaleString('en-US', {
      minimumFractionDigits: useCents ? 2 : 0,
      maximumFractionDigits: useCents ? 2 : 0,
    });
    return `${symbol}${formatted}`;
  }, [settings.currency, settings.numberFormat, settings.showCents]);

  const formatNumber = useCallback((num, decimals = 2) => {
    const n = parseFloat(num) || 0;
    if (settings.numberFormat === '1.234,56') {
      return n.toLocaleString('de-DE', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      });
    }
    return n.toLocaleString('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
  }, [settings.numberFormat]);

  const formatDate = useCallback((dateStr) => {
    if (!dateStr) return 'N/A';
    // Input: YYYY-MM-DD or MM/DD/YYYY style
    const str = String(dateStr);
    // Already in non-ISO format — return as-is
    if (!str.match(/^\d{4}-\d{2}-\d{2}$/)) return str;
    const [y, m, d] = str.split('-');
    switch (settings.dateFormat) {
      case 'DD/MM/YYYY': return `${d}/${m}/${y}`;
      case 'YYYY-MM-DD': return `${y}-${m}-${d}`;
      default:           return `${m}/${d}/${y}`; // MM/DD/YYYY
    }
  }, [settings.dateFormat]);

  // Row padding class based on table density
  const densityPadding = {
    Compact:     'py-1.5',
    Comfortable: 'py-3',
    Spacious:    'py-5',
  }[settings.tableDensity] || 'py-3';

  const currencySymbol = CURRENCY_SYMBOLS[settings.currency] || '$';

  return (
    <SettingsContext.Provider value={{
      settings,
      updateSettings,
      formatCurrency,
      formatNumber,
      formatDate,
      densityPadding,
      currencySymbol,
    }}>
      {children}
    </SettingsContext.Provider>
  );
}

export function useSettings() {
  const ctx = useContext(SettingsContext);
  if (!ctx) throw new Error('useSettings must be used within SettingsProvider');
  return ctx;
}

export default SettingsContext;
